import { motion, useReducedMotion } from "framer-motion";
import { useEffect, useRef, useState } from "react";

export const SPRING = { type: "spring", stiffness: 400, damping: 28 };
export const SOFT_SPRING = { type: "spring", stiffness: 260, damping: 30 };

export function useMotion() {
  const reduce = useReducedMotion();
  return {
    reduce,
    tap: reduce ? {} : { scale: 0.96 },
    hover: reduce ? {} : { y: -1 },
    hoverCard: reduce ? {} : { y: -2, boxShadow: "0 6px 18px rgba(10,10,10,0.08)" },
    fade: (dur = 0.1) => ({ duration: reduce ? 0.1 : dur }),
    view: reduce
      ? { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 }, transition: { duration: 0.1 } }
      : { initial: { opacity: 0, y: 8 }, animate: { opacity: 1, y: 0 }, exit: { opacity: 0 }, transition: { duration: 0.2 } },
    slideR: reduce
      ? { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 }, transition: { duration: 0.1 } }
      : { initial: { x: 28, opacity: 0 }, animate: { x: 0, opacity: 1 }, exit: { x: -28, opacity: 0 }, transition: { duration: 0.25 } },
    slideL: reduce
      ? { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 }, transition: { duration: 0.1 } }
      : { initial: { x: -28, opacity: 0 }, animate: { x: 0, opacity: 1 }, exit: { x: 28, opacity: 0 }, transition: { duration: 0.25 } },
    sheet: reduce
      ? { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 }, transition: { duration: 0.1 } }
      : { initial: { y: "100%" }, animate: { y: 0 }, exit: { y: "100%" }, transition: { duration: 0.3, ease: [0.32, 0.72, 0, 1] } },
  };
}

// botón animado estándar: spring al tocar, sube 1px al hover
export function MButton({ children, className = "", style, onClick, disabled, testid, title, type = "button" }) {
  const m = useMotion();
  return (
    <motion.button
      type={type}
      data-testid={testid}
      title={title}
      onClick={onClick}
      disabled={disabled}
      whileTap={m.tap}
      whileHover={m.hover}
      transition={SPRING}
      className={className}
      style={style}
    >
      {children}
    </motion.button>
  );
}

// número que cambia con conteo animado
export function RollingNumber({ value, duration = 0.8, className = "", format = (v) => Math.round(v), ...rest }) {
  const reduce = useReducedMotion();
  const [display, setDisplay] = useState(0);
  const fromRef = useRef(0);
  const curRef = useRef(0);
  const rafRef = useRef(null);

  useEffect(() => {
    const from = fromRef.current;
    const to = Number(value) || 0;
    if (reduce || duration <= 0) {
      setDisplay(to);
      fromRef.current = to;
      curRef.current = to;
      return;
    }
    const start = performance.now();
    const dur = duration * 1000;
    const tick = (now) => {
      const p = Math.min(1, (now - start) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      const cur = from + (to - from) * eased;
      curRef.current = cur;
      setDisplay(cur);
      if (p < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        fromRef.current = to;
      }
    };
    rafRef.current = requestAnimationFrame(tick);
    // si el valor cambia a mitad de la cuenta, la siguiente arranca desde donde iba (sin saltos)
    return () => { cancelAnimationFrame(rafRef.current); fromRef.current = curRef.current; };
  }, [value, duration, reduce]);

  // ...rest: data-testid y demás atributos llegan al span
  return <span className={className} {...rest}>{format(display)}</span>;
}
